import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

// SFMC deep link helpers
function extractTssd(authBaseUri) {
  if (!authBaseUri) return null;
  const m = authBaseUri.match(/\/\/(.*?)\.auth\.marketingcloudapis\.com/);
  return m ? m[1] : null;
}

function buildSfmcLink(item) {
  if (!item || typeof item === 'string') return null;
  const base = 'https://mc.exacttarget.com';
  switch (item.type) {
    case 'automation':
      return item.id ? `${base}/cloud/#app/Automation%20Studio/AutomationStudioFuel3/%23${item.id}` : null;
    case 'journey':
      return item.id ? `${base}/cloud/#app/Journey%20Builder/%23${item.id}` : null;
    case 'dataExtension':
      return `${base}/cloud/#app/Contact%20Builder`;
    case 'triggeredSend':
      return `${base}/cloud/#app/Email%20Studio`;
    default:
      return null;
  }
}

function formatItemDetail(item) {
  if (!item || typeof item === 'string') return null;
  const parts = [];
  if (item.status) parts.push(`Status: ${item.status}`);
  if (item.queuedCount !== undefined) parts.push(`Queued: ${item.queuedCount.toLocaleString()}`);
  if (item.rowCount !== undefined) parts.push(`Rows: ${item.rowCount.toLocaleString()}`);
  if (item.lastRunTime) parts.push(`Last run: ${new Date(item.lastRunTime).toLocaleDateString()}`);
  if (item.modifiedDate) parts.push(`Modified: ${new Date(item.modifiedDate).toLocaleDateString()}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function Audit() {
  const { darkMode } = useTheme();
  const { token } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [audit, setAudit] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [taskFilter, setTaskFilter] = useState('open');
  const [expandedFindings, setExpandedFindings] = useState({});

  // Fetch accounts
  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get('/api/sfmc-accounts');
        setAccounts(res.data || []);
        if (res.data.length > 0) {
          setSelectedAccountId(res.data[0].id);
        }
      } catch (err) {
        setError('Failed to load SFMC accounts');
      } finally {
        setLoading(false);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [token]);

  // Fetch audit data when account changes
  const fetchAuditData = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      const [historyRes, tasksRes, scoreRes] = await Promise.all([
        axios.get(`/api/audit?sfmcAccountId=${selectedAccountId}&limit=10`),
        axios.get(`/api/audit/tasks/list?sfmcAccountId=${selectedAccountId}&status=${taskFilter}`),
        axios.get(`/api/audit/health-score/history?sfmcAccountId=${selectedAccountId}&weeks=12`),
      ]);

      setAuditHistory(historyRes.data || []);
      setTasks(tasksRes.data || []);
      setScoreHistory(scoreRes.data || []);

      // Load the most recent completed audit
      const completed = (historyRes.data || []).find(a => a.status === 'completed');
      if (completed) {
        const auditRes = await axios.get(`/api/audit/${completed.id}`);
        setAudit(auditRes.data);
        setExpandedFindings({});
      }
    } catch (err) {
      console.error('Failed to load audit data:', err);
    }
  }, [selectedAccountId, taskFilter]);

  useEffect(() => {
    if (selectedAccountId) fetchAuditData();
  }, [selectedAccountId, fetchAuditData]);

  // Run audit
  const runAudit = async () => {
    if (!selectedAccountId) return;
    setRunning(true);
    setError('');

    try {
      const res = await axios.post('/api/audit/run', { sfmcAccountId: selectedAccountId });
      const auditId = res.data.auditId;

      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`/api/audit/${auditId}`);
          if (statusRes.data.status === 'completed') {
            clearInterval(poll);
            setAudit(statusRes.data);
            setExpandedFindings({});
            setRunning(false);
            fetchAuditData();
          } else if (statusRes.data.status === 'failed') {
            clearInterval(poll);
            setError(`Audit failed: ${statusRes.data.error_message}`);
            setRunning(false);
          }
        } catch (err) {
          clearInterval(poll);
          setError('Failed to check audit status');
          setRunning(false);
        }
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start audit');
      setRunning(false);
    }
  };

  // Download PDF
  const downloadPdf = async (auditId) => {
    try {
      const res = await axios.get(`/api/audit/${auditId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sfmc-audit-${auditId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  // Update task status
  const updateTask = async (taskId, status) => {
    try {
      await axios.patch(`/api/audit/tasks/${taskId}`, { status });
      fetchAuditData();
    } catch (err) {
      setError('Failed to update task');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#28a745';
    if (score >= 60) return '#ffc107';
    if (score >= 40) return '#fd7e14';
    return '#dc3545';
  };

  const getGrade = (score) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  const categoryLabels = {
    automations: { label: 'Automation Health', icon: '⚙️' },
    journeys: { label: 'Journey Health', icon: '🗺️' },
    dataExtensions: { label: 'Data Extensions', icon: '📦' },
    triggeredSends: { label: 'Triggered Sends', icon: '📨' },
    deliverability: { label: 'Email Deliverability', icon: '📊' },
    contactCapacity: { label: 'Contact Capacity', icon: '👥' },
    monitoringCoverage: { label: 'Monitoring Coverage', icon: '🔍' },
  };

  const priorityColors = {
    critical: '#dc3545',
    high: '#fd7e14',
    medium: '#ffc107',
    low: '#28a745',
  };

  if (loading) {
    return (
      <div>
        <div className="content-header"><h1>Operations Audit</h1></div>
        <div className="content-body"><div className="card"><p>Loading...</p></div></div>
      </div>
    );
  }

  const categories = audit?.categories ? (typeof audit.categories === 'string' ? JSON.parse(audit.categories) : audit.categories) : null;
  const summary = audit?.summary ? (typeof audit.summary === 'string' ? JSON.parse(audit.summary) : audit.summary) : null;
  const selectedAccount = accounts.find(a => a.id === parseInt(selectedAccountId));
  const tssd = selectedAccount ? extractTssd(selectedAccount.auth_base_uri) : null;

  return (
    <div>
      <div className="content-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h1>Operations Audit</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {accounts.length > 1 && (
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="form-select"
                style={{ minWidth: '200px' }}
              >
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.account_name}</option>
                ))}
              </select>
            )}
            <button
              className="btn btn-primary"
              onClick={runAudit}
              disabled={running || !selectedAccountId}
            >
              {running ? 'Running Audit...' : 'Run Audit'}
            </button>
          </div>
        </div>
      </div>

      <div className="content-body">
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            {error}
            <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>×</button>
          </div>
        )}

        {running && (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔍</div>
            <h3>Audit in Progress</h3>
            <p style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>
              Scanning your SFMC environment across 7 categories...
            </p>
            <div style={{
              width: '200px', height: '4px', margin: '20px auto',
              background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '2px', overflow: 'hidden'
            }}>
              <div style={{
                width: '40%', height: '100%', background: '#1a73e8', borderRadius: '2px',
                animation: 'shimmer 1.5s ease-in-out infinite'
              }} />
            </div>
          </div>
        )}

        {!running && audit && categories && (
          <>
            {/* Overall Score Card */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '30px', flexWrap: 'wrap' }}>
                {/* Score Circle */}
                <div style={{ position: 'relative', width: '120px', height: '120px', flexShrink: 0 }}>
                  <svg viewBox="0 0 120 120" style={{ width: '120px', height: '120px' }}>
                    <circle cx="60" cy="60" r="52" fill="none" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="8" />
                    <circle
                      cx="60" cy="60" r="52" fill="none"
                      stroke={getScoreColor(audit.overall_score)}
                      strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={`${(audit.overall_score / 100) * 327} 327`}
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: getScoreColor(audit.overall_score) }}>
                      {audit.overall_score}
                    </div>
                    <div style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>/100</div>
                  </div>
                </div>

                {/* Score Details */}
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <h2 style={{ margin: '0 0 5px 0' }}>
                    Grade: {getGrade(audit.overall_score)}
                    <span style={{
                      marginLeft: '10px', padding: '2px 8px', borderRadius: '4px', fontSize: '14px',
                      background: getScoreColor(audit.overall_score) + '20',
                      color: getScoreColor(audit.overall_score),
                    }}>
                      {audit.overall_score >= 80 ? 'Healthy' : audit.overall_score >= 60 ? 'Needs Attention' : 'Critical'}
                    </span>
                  </h2>
                  <p style={{ margin: '0', color: darkMode ? '#9ca3af' : '#6b7280', fontSize: '14px' }}>
                    {summary?.criticalIssues || 0} critical · {summary?.warnings || 0} warnings · {summary?.strengths?.length || 0} strengths
                  </p>
                  <p style={{ margin: '5px 0 0', color: darkMode ? '#6b7280' : '#9ca3af', fontSize: '12px' }}>
                    Last audit: {audit.completed_at ? new Date(audit.completed_at).toLocaleString() : 'N/A'}
                  </p>
                </div>

                {/* Download PDF */}
                <button
                  className="btn btn-secondary"
                  onClick={() => downloadPdf(audit.id)}
                  style={{ flexShrink: 0 }}
                >
                  📄 Download PDF
                </button>
              </div>
            </div>

            {/* Category Score Cards */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '12px', marginBottom: '20px'
            }}>
              {Object.entries(categoryLabels).map(([key, { label, icon }]) => {
                const cat = categories[key];
                if (!cat) return null;
                return (
                  <div key={key} className="card" style={{ padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>{icon}</div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: getScoreColor(cat.score) }}>
                      {cat.score}
                    </div>
                    <div style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280', marginTop: '3px' }}>
                      {label}
                    </div>
                    {/* Mini bar */}
                    <div style={{
                      width: '100%', height: '4px', borderRadius: '2px', marginTop: '8px',
                      background: darkMode ? '#374151' : '#e5e7eb'
                    }}>
                      <div style={{
                        width: `${cat.score}%`, height: '100%', borderRadius: '2px',
                        background: getScoreColor(cat.score),
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Score Trend Chart */}
            {scoreHistory.length > 1 && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <h3 style={{ marginTop: 0 }}>Score Trend</h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '100px', padding: '10px 0' }}>
                  {scoreHistory.slice().reverse().map((h, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: '100%', maxWidth: '40px',
                        height: `${h.score}%`, minHeight: '4px',
                        background: getScoreColor(h.score),
                        borderRadius: '2px 2px 0 0',
                        transition: 'height 0.3s ease'
                      }} />
                      <div style={{ fontSize: '10px', color: darkMode ? '#6b7280' : '#9ca3af', marginTop: '4px' }}>
                        {new Date(h.recorded_at).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Findings */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginTop: 0 }}>Detailed Findings</h3>
              {Object.entries(categoryLabels).map(([key, { label, icon }]) => {
                const cat = categories[key];
                if (!cat || !cat.findings || cat.findings.length === 0) return null;
                return (
                  <details key={key} style={{ marginBottom: '12px' }}>
                    <summary style={{
                      cursor: 'pointer', padding: '8px 12px', borderRadius: '6px',
                      background: darkMode ? '#1f2937' : '#f9fafb',
                      fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                      <span>{icon}</span>
                      <span>{label}</span>
                      <span style={{
                        marginLeft: 'auto', fontWeight: 'bold',
                        color: getScoreColor(cat.score)
                      }}>{cat.score}/100</span>
                    </summary>
                    <div style={{ padding: '10px 12px 0' }}>
                      {cat.findings.map((f, i) => {
                        const findingKey = `${key}-${i}`;
                        const hasItems = f.items && f.items.length > 0;
                        const isExpanded = expandedFindings[findingKey];
                        return (
                          <div key={i} style={{ padding: '2px 0' }}>
                            <div
                              style={{
                                display: 'flex', alignItems: 'flex-start', gap: '8px',
                                padding: '6px 0', fontSize: '14px',
                                cursor: hasItems ? 'pointer' : 'default',
                              }}
                              onClick={() => hasItems && setExpandedFindings(prev => ({ ...prev, [findingKey]: !prev[findingKey] }))}
                            >
                              <span style={{
                                width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, marginTop: '5px',
                                background: f.level === 'error' ? '#dc3545' : f.level === 'warning' ? '#fd7e14' : f.level === 'healthy' ? '#28a745' : '#6b7280'
                              }} />
                              <span style={{ color: darkMode ? '#d1d5db' : '#374151', flex: 1 }}>{f.message}</span>
                              {hasItems && (
                                <span style={{ color: darkMode ? '#6b7280' : '#9ca3af', fontSize: '12px', flexShrink: 0 }}>
                                  {isExpanded ? '▾' : '▸'} {f.items.length}
                                </span>
                              )}
                            </div>
                            {hasItems && isExpanded && (
                              <div style={{
                                marginLeft: '16px', padding: '4px 12px',
                                borderLeft: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                                marginBottom: '4px',
                              }}>
                                {f.items.map((item, j) => {
                                  const isObj = typeof item === 'object' && item !== null;
                                  const name = isObj ? item.name : item;
                                  const detail = isObj ? formatItemDetail(item) : null;
                                  const link = isObj ? buildSfmcLink(item) : null;
                                  return (
                                    <div key={j} style={{
                                      display: 'flex', alignItems: 'center', gap: '8px',
                                      padding: '4px 0', fontSize: '13px',
                                      borderBottom: j < f.items.length - 1 ? `1px solid ${darkMode ? '#1f2937' : '#f3f4f6'}` : 'none',
                                    }}>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ color: darkMode ? '#d1d5db' : '#374151' }}>{name}</div>
                                        {detail && <div style={{ color: darkMode ? '#6b7280' : '#9ca3af', fontSize: '11px', marginTop: '1px' }}>{detail}</div>}
                                      </div>
                                      {link && (
                                        <a href={link} target="_blank" rel="noopener noreferrer"
                                          onClick={e => e.stopPropagation()}
                                          style={{ color: '#1a73e8', fontSize: '12px', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                                          title="Open in SFMC"
                                        >Open ↗</a>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {cat.recommendations && cat.recommendations.length > 0 && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                          {cat.recommendations.map((r, i) => (
                            <div key={i} style={{ fontSize: '13px', color: darkMode ? '#9ca3af' : '#6b7280', padding: '3px 0' }}>
                              → {r}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          </>
        )}

        {!running && !audit && (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔍</div>
            <h3>No Audit Data Yet</h3>
            <p style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>
              Run your first operations audit to get a comprehensive health score and recommendations for your SFMC environment.
            </p>
            <button className="btn btn-primary" onClick={runAudit} disabled={!selectedAccountId}>
              Run First Audit
            </button>
          </div>
        )}

        {/* Action Items */}
        {tasks.length > 0 && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Action Items ({tasks.length})</h3>
              <select
                value={taskFilter}
                onChange={(e) => setTaskFilter(e.target.value)}
                className="form-select"
                style={{ width: 'auto', minWidth: '120px' }}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
                <option value="all">All</option>
              </select>
            </div>

            {tasks.map(task => (
              <div key={task.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px', borderRadius: '6px', marginBottom: '8px',
                background: darkMode ? '#1f2937' : '#f9fafb',
                opacity: task.status === 'resolved' || task.status === 'dismissed' ? 0.6 : 1,
              }}>
                <span style={{
                  width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
                  background: priorityColors[task.priority] || '#999'
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: darkMode ? '#d1d5db' : '#374151' }}>
                    {task.title}
                  </div>
                  <div style={{ fontSize: '11px', color: darkMode ? '#6b7280' : '#9ca3af' }}>
                    {task.priority} · {task.category}
                  </div>
                </div>
                {task.status === 'open' && (
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      className="btn btn-sm"
                      onClick={() => updateTask(task.id, 'resolved')}
                      style={{ fontSize: '12px', padding: '4px 8px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      ✓ Done
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => updateTask(task.id, 'dismissed')}
                      style={{ fontSize: '12px', padding: '4px 8px', background: darkMode ? '#374151' : '#e5e7eb', color: darkMode ? '#9ca3af' : '#6b7280', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Audit History */}
        {auditHistory.length > 0 && (
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Audit History</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                  <th style={{ padding: '8px 0', fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Date</th>
                  <th style={{ padding: '8px 0', fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Score</th>
                  <th style={{ padding: '8px 0', fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Status</th>
                  <th style={{ padding: '8px 0', fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {auditHistory.map(h => (
                  <tr key={h.id} style={{ borderBottom: `1px solid ${darkMode ? '#1f2937' : '#f3f4f6'}` }}>
                    <td style={{ padding: '8px 0', fontSize: '14px' }}>
                      {new Date(h.started_at).toLocaleString('en', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '8px 0' }}>
                      {h.overall_score != null ? (
                        <span style={{ fontWeight: 'bold', color: getScoreColor(h.overall_score) }}>
                          {h.overall_score}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '8px 0' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                        background: h.status === 'completed' ? '#28a74520' : h.status === 'failed' ? '#dc354520' : '#ffc10720',
                        color: h.status === 'completed' ? '#28a745' : h.status === 'failed' ? '#dc3545' : '#ffc107'
                      }}>
                        {h.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 0', textAlign: 'right' }}>
                      {h.status === 'completed' && (
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            onClick={async () => {
                              const res = await axios.get(`/api/audit/${h.id}`);
                              setAudit(res.data);
                              setExpandedFindings({});
                            }}
                            style={{ fontSize: '12px', color: '#1a73e8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            View
                          </button>
                          <button
                            onClick={() => downloadPdf(h.id)}
                            style={{ fontSize: '12px', color: '#1a73e8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            PDF
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
        details > summary { list-style: none; }
        details > summary::-webkit-details-marker { display: none; }
        details > summary::before {
          content: '▸ ';
          transition: transform 0.2s;
        }
        details[open] > summary::before {
          content: '▾ ';
        }
      `}</style>
    </div>
  );
}

export default Audit;
